// src/components/communications/ClientSidebar.jsx
// Right panel - Client details sidebar with quick actions
import React, { useEffect } from 'react';
import {
  Phone, MessageSquare, Mail, Calendar, ExternalLink,
  Shield, AlertTriangle, Clock, FileText, User,
  MapPin, CreditCard, Heart
} from 'lucide-react';

// Get initials from name
const getInitials = (firstName, lastName) => {
  const first = firstName?.[0] || '';
  const last = lastName?.[0] || '';
  return (first + last).toUpperCase() || '?';
};

// Get risk level color and label
const getRiskInfo = (score) => {
  if (!score && score !== 0) return { color: 'gray', bgColor: 'bg-gray-200', label: 'Unknown', textColor: 'text-gray-600' };
  if (score >= 75) return { color: 'red', bgColor: 'bg-red-500', label: 'Critical', textColor: 'text-red-600' };
  if (score >= 50) return { color: 'orange', bgColor: 'bg-orange-500', label: 'High', textColor: 'text-orange-600' };
  if (score >= 25) return { color: 'yellow', bgColor: 'bg-yellow-500', label: 'Moderate', textColor: 'text-yellow-600' };
  return { color: 'green', bgColor: 'bg-green-500', label: 'Low', textColor: 'text-green-600' };
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export default function ClientSidebar({
  client,
  onNavigateToProfile,
  onComposeChange,
  onRefreshClient
}) {
  // Listen for risk-score-updated events to refresh client data
  useEffect(() => {
    if (!client?.id || !onRefreshClient) return;

    const handleRiskUpdate = (e) => {
      if (e.detail?.clientId === client.id) {
        onRefreshClient(client.id);
      }
    };

    window.addEventListener('risk-score-updated', handleRiskUpdate);
    return () => window.removeEventListener('risk-score-updated', handleRiskUpdate);
  }, [client?.id, onRefreshClient]);

  if (!client) return null;

  const clientName = `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Client';
  const initials = getInitials(client.firstName, client.lastName);
  const riskInfo = getRiskInfo(client.riskScore);

  // Calculate age if DOB exists
  const age = client.dateOfBirth
    ? Math.floor((new Date() - new Date(client.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <div className="w-96 border-l border-gray-200 bg-white flex flex-col h-full overflow-hidden">
      {/* Client Header */}
      <div className="p-6 border-b border-gray-200 text-center bg-gradient-to-b from-slate-50 to-white">
        {/* Avatar */}
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3 shadow-lg">
          {initials}
        </div>

        {/* Name */}
        <h2 className="text-xl font-bold text-gray-900 mb-1">{clientName}</h2>

        {/* Contact Info */}
        {client.phone && (
          <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
            <Phone className="w-3 h-3" />
            {client.phone}
          </p>
        )}
        {client.email && (
          <p className="text-sm text-gray-500 flex items-center justify-center gap-1 mt-0.5">
            <Mail className="w-3 h-3" />
            <span className="truncate max-w-[200px]">{client.email}</span>
          </p>
        )}

        {/* Age & Location */}
        <div className="flex items-center justify-center gap-3 mt-2 text-xs text-gray-400">
          {age && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {age} years old
            </span>
          )}
          {(client.city || client.state) && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {[client.city, client.state].filter(Boolean).join(', ')}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Risk Score */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Risk Score
            </span>
            <div className="flex items-center gap-2">
              <span className={`text-lg font-bold ${riskInfo.textColor}`}>
                {client.riskScore ?? '—'}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                riskInfo.color === 'red' ? 'bg-red-100 text-red-700' :
                riskInfo.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                riskInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                riskInfo.color === 'green' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {riskInfo.label}
              </span>
            </div>
          </div>
          {/* Risk Bar */}
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${riskInfo.bgColor}`}
              style={{ width: `${Math.min(100, client.riskScore || 0)}%` }}
            />
          </div>
          {client.riskFactors && client.riskFactors.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {client.riskFactors.length} risk factor{client.riskFactors.length !== 1 ? 's' : ''} detected
              </p>
            </div>
          )}
        </div>

        {/* Current Policy */}
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Current Policy
          </h3>
          {client.carrier || client.plan ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Carrier</span>
                <span className="text-sm font-medium text-gray-900">{client.carrier || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Plan</span>
                <span className="text-sm font-medium text-gray-900 text-right max-w-[150px] truncate">
                  {client.plan || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Type</span>
                <span className="text-sm font-medium text-gray-900">{client.planType || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Effective</span>
                <span className="text-sm font-medium text-gray-900">{formatDate(client.effectiveDate)}</span>
              </div>
              {client.monthlyPremium && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Premium</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(client.monthlyPremium)}/mo
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No policy on file</p>
          )}
        </div>

        {/* Medicare Info */}
        {client.medicareNumber && (
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Heart className="w-4 h-4" />
              Medicare Info
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">MBI</span>
                <span className="text-sm font-medium text-gray-900 font-mono">
                  {client.medicareNumber}
                </span>
              </div>
              {client.partADate && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Part A</span>
                  <span className="text-sm font-medium text-gray-900">{formatDate(client.partADate)}</span>
                </div>
              )}
              {client.partBDate && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Part B</span>
                  <span className="text-sm font-medium text-gray-900">{formatDate(client.partBDate)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Last Contact */}
        {client.lastContactDate && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Last Contact
              </span>
              <span className="text-sm font-medium text-gray-900">
                {formatDate(client.lastContactDate)}
              </span>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onComposeChange('call')}
              className="flex flex-col items-center gap-1 p-3 bg-blue-50 rounded-lg text-blue-600 hover:bg-blue-100 transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span className="text-xs font-medium">Call</span>
            </button>
            <button
              onClick={() => onComposeChange('sms')}
              className="flex flex-col items-center gap-1 p-3 bg-green-50 rounded-lg text-green-600 hover:bg-green-100 transition-colors"
            >
              <MessageSquare className="w-5 h-5" />
              <span className="text-xs font-medium">Text</span>
            </button>
            <button
              onClick={() => onComposeChange('email')}
              className="flex flex-col items-center gap-1 p-3 bg-red-50 rounded-lg text-red-600 hover:bg-red-100 transition-colors"
            >
              <Mail className="w-5 h-5" />
              <span className="text-xs font-medium">Email</span>
            </button>
            <button
              onClick={onNavigateToProfile}
              className="flex flex-col items-center gap-1 p-3 bg-amber-50 rounded-lg text-amber-600 hover:bg-amber-100 transition-colors"
            >
              <Calendar className="w-5 h-5" />
              <span className="text-xs font-medium">Schedule</span>
            </button>
          </div>
        </div>

        {/* Notes Preview */}
        {client.notes && (
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
            <p className="text-sm text-gray-600 line-clamp-3">{client.notes}</p>
          </div>
        )}
      </div>

      {/* View Full Profile Button */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={onNavigateToProfile}
          className="w-full flex items-center justify-center gap-2 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
        >
          <ExternalLink className="w-4 h-4" />
          View Full Profile
        </button>
      </div>
    </div>
  );
}
